import type { ComputedRef } from 'vue'

export interface ToolItem {
  icon: string
  titleKey: string
  descKey: string
  ctaKey: string
  path: string
  recommended?: boolean
}

export interface ToolCategory {
  id: string
  icon: string
  titleKey: string
  subtitleKey: string
  sectionPath: string
  group?: ToolDirectoryGroupId
  tools: ToolItem[]
}

export type ToolDirectoryGroupId = 'charting' | 'fortune-analysis' | 'special'

export interface ToolDirectoryLink {
  icon: string
  titleKey: string
  subtitleKey?: string
  path: string
  kind: 'tool' | 'topic'
}

export interface ToolDirectoryGroup {
  id: ToolDirectoryGroupId
  icon: string
  titleKey: string
  subtitleKey: string
  path: string
  links: ToolDirectoryLink[]
}

const chartingToolCategory: ToolCategory = {
  id: 'paipan',
  icon: 'i-heroicons-table-cells',
  titleKey: 'paipanTopic.title',
  subtitleKey: 'paipanTopic.subtitle',
  sectionPath: '/chart',
  group: 'charting',
  tools: [
    {
      icon: 'i-heroicons-calendar-days',
      titleKey: 'baziChart.title',
      descKey: 'baziChart.subtitle',
      ctaKey: 'baziChart.submit',
      path: '/tools/bazi-paipan',
      recommended: true,
    },
    {
      icon: 'i-heroicons-globe-asia-australia',
      titleKey: 'thaiAstro.title',
      descKey: 'thaiAstro.subtitle',
      ctaKey: 'thaiAstro.submit',
      path: '/tools/thai-astro-paipan',
    },
    {
      icon: 'i-heroicons-squares-2x2',
      titleKey: 'humanDesign.title',
      descKey: 'humanDesign.cardDescription',
      ctaKey: 'humanDesign.submit',
      path: '/tools/human-design',
    },
    {
      icon: 'i-heroicons-calendar-date-range',
      titleKey: 'shichuSuimei.title',
      descKey: 'shichuSuimei.subtitle',
      ctaKey: 'shichuSuimei.submit',
      path: '/tools/shichu-suimei',
    },
    {
      icon: 'i-heroicons-sparkles',
      titleKey: 'qizhengChart.title',
      descKey: 'qizhengChart.subtitle',
      ctaKey: 'qizhengChart.submit',
      path: '/tools/qizheng-paipan',
    },
    {
      icon: 'i-heroicons-viewfinder-circle',
      titleKey: 'kyuseiKigaku.title',
      descKey: 'kyuseiKigaku.subtitle',
      ctaKey: 'kyuseiKigaku.submit',
      path: '/tools/kyusei-kigaku',
    },
    {
      icon: 'i-heroicons-globe-alt',
      titleKey: 'natalAstro.title',
      descKey: 'natalAstro.subtitle',
      ctaKey: 'natalAstro.submit',
      path: '/tools/natal-astro',
    },
    {
      icon: 'i-heroicons-square-3-stack-3d',
      titleKey: 'qimenPaipan.title',
      descKey: 'qimenPaipan.subtitle',
      ctaKey: 'qimenPaipan.submit',
      path: '/tools/qimen-paipan',
    },
    {
      icon: 'i-heroicons-calculator',
      titleKey: 'tiebanPaipan.title',
      descKey: 'tiebanPaipan.subtitle',
      ctaKey: 'tiebanPaipan.submit',
      path: '/tools/tieban-paipan',
    },
    {
      icon: 'i-heroicons-circle-stack',
      titleKey: 'huangjiPaipan.title',
      descKey: 'huangjiPaipan.subtitle',
      ctaKey: 'huangjiPaipan.submit',
      path: '/tools/huangji-paipan',
    },
    {
      icon: 'i-heroicons-circle-stack',
      titleKey: 'liurenPaipan.title',
      descKey: 'liurenPaipan.subtitle',
      ctaKey: 'liurenPaipan.submit',
      path: '/tools/liuren-paipan',
    },
    {
      icon: 'i-heroicons-viewfinder-circle',
      titleKey: 'taiyiPaipan.title',
      descKey: 'taiyiPaipan.subtitle',
      ctaKey: 'taiyiPaipan.submit',
      path: '/tools/taiyi-paipan',
    },
    {
      icon: 'i-heroicons-sparkles',
      titleKey: 'ziweiChart.title',
      descKey: 'ziweiChart.subtitle',
      ctaKey: 'ziweiChart.submit',
      path: '/tools/ziwei-paipan',
    },
    {
      icon: 'i-heroicons-moon',
      titleKey: 'sukuyoPaipan.title',
      descKey: 'sukuyoPaipan.subtitle',
      ctaKey: 'sukuyoPaipan.submit',
      path: '/tools/sukuyo-paipan',
    },
    {
      icon: 'i-heroicons-squares-2x2',
      titleKey: 'zibaifeixingPaipan.title',
      descKey: 'zibaifeixingPaipan.subtitle',
      ctaKey: 'zibaifeixingPaipan.submit',
      path: '/tools/zibaifeixing-paipan',
    },
    {
      icon: 'i-heroicons-viewfinder-circle',
      titleKey: 'fengshuiLuopan.title',
      descKey: 'fengshuiLuopan.subtitle',
      ctaKey: 'fengshuiLuopan.action.start',
      path: '/tools/fengshui-luopan',
    },
    {
      icon: 'i-heroicons-globe-asia-australia',
      titleKey: 'vpc.title',
      descKey: 'vpc.subtitle',
      ctaKey: 'common.start',
      path: '/tools/vedic-astro',
    },
  ],
}

const analysisToolCategories: ToolCategory[] = [
  {
    id: 'numeric-energy',
    icon: 'i-heroicons-calculator',
    titleKey: 'numericEnergyTopic.title',
    subtitleKey: 'numericEnergyTopic.subtitle',
    sectionPath: '/numeric-energy',
    tools: [
      {
        icon: 'i-heroicons-device-phone-mobile',
        titleKey: 'numericEnergy.scenarios.phone.longTitle',
        descKey: 'numericEnergy.scenarios.phone.description',
        ctaKey: 'numericEnergy.scenarios.phone.cta',
        path: '/tools/numeric-energy-phone',
        recommended: true,
      },
      {
        icon: 'i-heroicons-truck',
        titleKey: 'numericEnergy.scenarios.plate.longTitle',
        descKey: 'numericEnergy.scenarios.plate.description',
        ctaKey: 'numericEnergy.scenarios.plate.cta',
        path: '/tools/numeric-energy-plate',
      },
      {
        icon: 'i-heroicons-home-modern',
        titleKey: 'numericEnergy.scenarios.door.longTitle',
        descKey: 'numericEnergy.scenarios.door.description',
        ctaKey: 'numericEnergy.scenarios.door.cta',
        path: '/tools/numeric-energy-door',
      },
      {
        icon: 'i-heroicons-credit-card',
        titleKey: 'numericEnergy.scenarios.card.longTitle',
        descKey: 'numericEnergy.scenarios.card.description',
        ctaKey: 'numericEnergy.scenarios.card.cta',
        path: '/tools/numeric-energy-card',
      },
    ],
  },
  {
    id: 'fortune-telling',
    icon: 'i-heroicons-sparkles',
    titleKey: 'tools.categoryFortuneTelling',
    subtitleKey: 'tools.categoryFortuneTellingSubtitle',
    sectionPath: '/fortune-telling',
    tools: [
      {
        icon: 'i-heroicons-sparkles',
        titleKey: 'horoscope.title',
        descKey: 'horoscope.cardDescription',
        ctaKey: 'horoscope.cardCta',
        path: '/fortune-telling/horoscope',
        recommended: true,
      },
      {
        icon: 'i-heroicons-calendar-days',
        titleKey: 'home.toolBaziTitle',
        descKey: 'home.toolBaziDesc',
        ctaKey: 'home.toolBaziCta',
        path: '/tools/bazi',
        recommended: true,
      },
      {
        icon: 'i-heroicons-chart-bar',
        titleKey: 'baziElements.title',
        descKey: 'baziElements.subtitle',
        ctaKey: 'baziElements.submit',
        path: '/tools/bazi-elements',
      },
      {
        icon: 'i-heroicons-scale',
        titleKey: 'home.toolZipingBaziTitle',
        descKey: 'home.toolZipingBaziDesc',
        ctaKey: 'home.toolZipingBaziCta',
        path: '/tools/ziping-bazi',
      },
      {
        icon: 'i-heroicons-viewfinder-circle',
        titleKey: 'mangpaiBazi.title',
        descKey: 'mangpaiBazi.subtitle',
        ctaKey: 'mangpaiBazi.cta',
        path: '/tools/mangpai-bazi',
      },
      {
        icon: 'i-heroicons-sparkles',
        titleKey: 'newSchoolBazi.title',
        descKey: 'newSchoolBazi.subtitle',
        ctaKey: 'newSchoolBazi.cta',
        path: '/tools/new-school-bazi',
      },
      {
        icon: 'i-heroicons-map-pin',
        titleKey: 'zecheng.title',
        descKey: 'zecheng.subtitle',
        ctaKey: 'zecheng.cta',
        path: '/tools/zecheng',
      },
      {
        icon: 'i-heroicons-sun',
        titleKey: 'thaiBuddha.title',
        descKey: 'thaiBuddha.subtitle',
        ctaKey: 'thaiBuddha.submit',
        path: '/tools/thai-buddha',
      },
      {
        icon: 'i-heroicons-star',
        titleKey: 'rokusei.title',
        descKey: 'rokusei.subtitle',
        ctaKey: 'rokusei.submit',
        path: '/tools/rokusei-senjutsu',
      },
      {
        icon: 'i-heroicons-squares-plus',
        titleKey: 'home.toolBaziZiweiTitle',
        descKey: 'home.toolBaziZiweiDesc',
        ctaKey: 'home.toolBaziZiweiCta',
        path: '/tools/bazi-ziwei',
      },
      {
        icon: 'i-heroicons-sparkles',
        titleKey: 'zwds.title',
        descKey: 'seo.zwdsDesc',
        ctaKey: 'common.start',
        path: '/tools/zwds',
      },
      {
        icon: 'i-heroicons-circle-stack',
        titleKey: 'home.toolLiuyaoDivinationTitle',
        descKey: 'home.toolLiuyaoDivinationDesc',
        ctaKey: 'home.toolLiuyaoDivinationCta',
        path: '/tools/liuyao-divination',
      },
      {
        icon: 'i-heroicons-sparkles',
        titleKey: 'zhouyi.title',
        descKey: 'seo.zhouyiDesc',
        ctaKey: 'common.start',
        path: '/tools/zhouyi',
      },
      {
        icon: 'i-heroicons-map',
        titleKey: 'home.toolQimenTitle',
        descKey: 'home.toolQimenDesc',
        ctaKey: 'home.toolQimenCta',
        path: '/tools/qimen',
      },
      {
        icon: 'i-heroicons-square-3-stack-3d',
        titleKey: 'home.toolTaiyiTitle',
        descKey: 'home.toolTaiyiDesc',
        ctaKey: 'home.toolTaiyiCta',
        path: '/tools/taiyi',
      },
      {
        icon: 'i-heroicons-globe-asia-australia',
        titleKey: 'home.toolHuangjiZhinianguaTitle',
        descKey: 'home.toolHuangjiZhinianguaDesc',
        ctaKey: 'home.toolHuangjiZhinianguaCta',
        path: '/tools/huangji-zhiniangua',
      },
      {
        icon: 'i-heroicons-eye',
        titleKey: 'home.toolLiurenTitle',
        descKey: 'home.toolLiurenDesc',
        ctaKey: 'home.toolLiurenCta',
        path: '/tools/liuren',
      },
      {
        icon: 'i-heroicons-hand-raised',
        titleKey: 'home.toolXiaoLiurenTitle',
        descKey: 'home.toolXiaoLiurenDesc',
        ctaKey: 'home.toolXiaoLiurenCta',
        path: '/tools/xiao-liuren',
      },
      {
        icon: 'i-heroicons-scale',
        titleKey: 'home.toolJinkoujueTitle',
        descKey: 'home.toolJinkoujueDesc',
        ctaKey: 'home.toolJinkoujueCta',
        path: '/tools/jinkoujue',
      },
      {
        icon: 'i-heroicons-sparkles',
        titleKey: 'home.toolTarotTitle',
        descKey: 'home.toolTarotDesc',
        ctaKey: 'home.toolTarotCta',
        path: '/tools/tarot',
      },
      {
        icon: 'i-heroicons-sparkles',
        titleKey: 'thaiTarot.title',
        descKey: 'thaiTarot.subtitle',
        ctaKey: 'thaiTarot.submit',
        path: '/tools/thai-tarot',
      },
      {
        icon: 'i-heroicons-squares-plus',
        titleKey: 'home.toolLenormandTitle',
        descKey: 'home.toolLenormandDesc',
        ctaKey: 'home.toolLenormandCta',
        path: '/tools/lenormand',
      },
      {
        icon: 'i-heroicons-numbered-list',
        titleKey: 'home.toolNumerologyTitle',
        descKey: 'home.toolNumerologyDesc',
        ctaKey: 'home.toolNumerologyCta',
        path: '/tools/numerology',
      },
      {
        icon: 'i-heroicons-squares-2x2',
        titleKey: 'home.toolWugeTitle',
        descKey: 'home.toolWugeDesc',
        ctaKey: 'home.toolWugeCta',
        path: '/tools/wuge',
      },
      {
        icon: 'i-heroicons-scale',
        titleKey: 'home.toolChengguTitle',
        descKey: 'home.toolChengguDesc',
        ctaKey: 'home.toolChengguCta',
        path: '/tools/chenggu',
      },
      {
        icon: 'i-heroicons-heart',
        titleKey: 'home.toolBaziZhengyuanTitle',
        descKey: 'home.toolBaziZhengyuanDesc',
        ctaKey: 'home.toolBaziZhengyuanCta',
        path: '/tools/bazi-zhengyuan',
      },
      {
        icon: 'i-heroicons-banknotes',
        titleKey: 'home.toolBaziWealthTitle',
        descKey: 'home.toolBaziWealthDesc',
        ctaKey: 'home.toolBaziWealthCta',
        path: '/tools/bazi-wealth',
      },
      {
        icon: 'i-heroicons-sparkles',
        titleKey: 'home.toolBaziFortuneTuneTitle',
        descKey: 'home.toolBaziFortuneTuneDesc',
        ctaKey: 'home.toolBaziFortuneTuneCta',
        path: '/tools/bazi-fortune-tune',
      },
      {
        icon: 'i-heroicons-sparkles',
        titleKey: 'home.toolLuckyPlantTitle',
        descKey: 'home.toolLuckyPlantDesc',
      ctaKey: 'home.toolLuckyPlantCta',
      path: '/tools/lucky-plant',
    },
    {
      icon: 'i-heroicons-sparkles',
      titleKey: 'home.toolBirthBuddhaTitle',
      descKey: 'home.toolBirthBuddhaDesc',
      ctaKey: 'home.toolBirthBuddhaCta',
      path: '/tools/birth-buddha',
    },
    {
      icon: 'i-heroicons-calendar-days',
      titleKey: 'home.toolFortuneCalendarTitle',
      descKey: 'home.toolFortuneCalendarDesc',
      ctaKey: 'home.toolFortuneCalendarCta',
      path: '/tools/fortune-calendar',
    },
    {
      icon: 'i-heroicons-chat-bubble-left-right',
      titleKey: 'home.toolHumorHermitTitle',
      descKey: 'home.toolHumorHermitDesc',
      ctaKey: 'home.toolHumorHermitCta',
      path: '/tools/humor-hermit',
      recommended: true,
    },
   ],
 },
  {
    id: 'astrology',
    icon: 'i-heroicons-star',
    titleKey: 'tools.categoryAstrology',
    subtitleKey: 'tools.categoryAstrologySubtitle',
    sectionPath: '/astrology',
    tools: [
      {
        icon: 'i-heroicons-globe-asia-australia',
        titleKey: 'home.toolVedicTitle',
        descKey: 'home.toolVedicDesc',
        ctaKey: 'home.toolVedicCta',
        path: '/tools/vedic-astro',
        recommended: true,
      },
      {
        icon: 'i-heroicons-heart',
        titleKey: 'vedicHepan.title',
        descKey: 'vedicHepan.subtitle',
        ctaKey: 'vedicHepan.submitBtn',
        path: '/tools/vedic-hepan',
      },
      {
        icon: 'i-heroicons-briefcase',
        titleKey: 'vedicHepanCareer.title',
        descKey: 'vedicHepanCareer.subtitle',
        ctaKey: 'vedicHepanCareer.submitBtn',
        path: '/tools/vedic-hepan-career',
      },
      {
        icon: 'i-heroicons-users',
        titleKey: 'astroZhichangHepan.title',
        descKey: 'astroZhichangHepan.toolDesc',
        ctaKey: 'astroZhichangHepan.toolCta',
        path: '/tools/astro-zhichang-hepan',
      },
      {
        icon: 'i-heroicons-globe-alt',
        titleKey: 'home.toolAstroFortuneTuneTitle',
        descKey: 'home.toolAstroFortuneTuneDesc',
        ctaKey: 'home.toolAstroFortuneTuneCta',
        path: '/tools/astro-fortune-tune',
      },
      {
        icon: 'i-heroicons-star',
        titleKey: 'home.toolQizhengSiyuTitle',
        descKey: 'home.toolQizhengSiyuDesc',
        ctaKey: 'home.toolQizhengSiyuCta',
        path: '/tools/qizheng-siyu',
      },
      {
        icon: 'i-heroicons-cube',
        titleKey: 'astroDice.title',
        descKey: 'astroDice.subtitle',
        ctaKey: 'astroDice.rollBtn',
        path: '/tools/astro-dice',
      },
    ],
  },
  {
    id: 'fengshui',
    icon: 'i-heroicons-map',
    titleKey: 'tools.categoryFengshui',
    subtitleKey: 'tools.categoryFengshuiSubtitle',
    sectionPath: '/fengshui',
    tools: [
      {
        icon: 'i-heroicons-map',
        titleKey: 'home.toolFengshuiTitle',
        descKey: 'home.toolFengshuiDesc',
        ctaKey: 'home.toolFengshuiCta',
        path: '/tools/fengshui',
      },
      {
        icon: 'i-heroicons-table-cells',
        titleKey: 'home.toolXuankongFengshuiTitle',
        descKey: 'home.toolXuankongFengshuiDesc',
        ctaKey: 'home.toolXuankongFengshuiCta',
        path: '/tools/xuankong-fengshui',
      },
      {
        icon: 'i-heroicons-squares-2x2',
        titleKey: 'home.toolZibaifeixingTitle',
        descKey: 'home.toolZibaifeixingDesc',
        ctaKey: 'home.toolZibaifeixingCta',
        path: '/tools/zibaifeixing',
      },
      {
        icon: 'i-heroicons-viewfinder-circle',
        titleKey: 'home.toolJinsuoyuguanFengshuiTitle',
        descKey: 'home.toolJinsuoyuguanFengshuiDesc',
        ctaKey: 'home.toolJinsuoyuguanFengshuiCta',
        path: '/tools/jinsuoyuguan-fengshui',
      },
      {
        icon: 'i-heroicons-building-office-2',
        titleKey: 'bazhai.title',
        descKey: 'seo.bazhaiDesc',
        ctaKey: 'bazhai.submitBtn',
        path: '/tools/bazhai-fengshui',
      },
      {
        icon: 'i-heroicons-briefcase',
        titleKey: 'officeFengshui.title',
        descKey: 'seo.officeFengshuiDesc',
        ctaKey: 'officeFengshui.submitBtn',
        path: '/tools/office-fengshui',
      },
      {
        icon: 'i-heroicons-home',
        titleKey: 'hallFengshui.title',
        descKey: 'seo.hallFengshuiDesc',
        ctaKey: 'hallFengshui.submitBtn',
        path: '/tools/hall-fengshui',
      },
      {
        icon: 'i-heroicons-moon',
        titleKey: 'bedroomFengshui.title',
        descKey: 'seo.bedroomFengshuiDesc',
        ctaKey: 'bedroomFengshui.submitBtn',
        path: '/tools/bedroom-fengshui',
      },
      {
        icon: 'i-heroicons-academic-cap',
        titleKey: 'studyFengshui.title',
        descKey: 'seo.studyFengshuiDesc',
        ctaKey: 'studyFengshui.submitBtn',
        path: '/tools/study-fengshui',
      },
      {
        icon: 'i-heroicons-beaker',
        titleKey: 'fishTank.title',
        descKey: 'seo.fishTankDesc',
        ctaKey: 'fishTank.submitBtn',
        path: '/tools/fengshui-fish-tank',
      },
      {
        icon: 'i-heroicons-viewfinder-circle',
        titleKey: 'fengshuiOrnament.title',
        descKey: 'seo.fengshuiOrnamentDesc',
        ctaKey: 'fengshuiOrnament.submitBtn',
        path: '/tools/fengshui-ornament',
      },
    ],
  },
  {
    id: 'shuangren-hepan',
    icon: 'i-heroicons-heart',
    titleKey: 'tools.categoryDoubleChart',
    subtitleKey: 'tools.categoryDoubleChartSubtitle',
    sectionPath: '/shuangren-hepan',
    tools: [
      {
        icon: 'i-heroicons-heart',
        titleKey: 'lifePathPeidui.title',
        descKey: 'lifePathPeidui.subtitle',
        ctaKey: 'lifePathPeidui.submitBtn',
        path: '/tools/lifepath-peidui',
        recommended: true,
      },
      {
        icon: 'i-heroicons-briefcase',
        titleKey: 'baziShiyeHepan.title',
        descKey: 'baziShiyeHepan.toolDesc',
        ctaKey: 'baziShiyeHepan.toolCta',
        path: '/tools/bazi-shiye-hepan',
      },
      {
        icon: 'i-heroicons-users',
        titleKey: 'baziZhichangHepan.title',
        descKey: 'baziZhichangHepan.toolDesc',
        ctaKey: 'baziZhichangHepan.toolCta',
        path: '/tools/bazi-zhichang-hepan',
      },
      {
        icon: 'i-heroicons-briefcase',
        titleKey: 'ziweiShiyeHepan.title',
        descKey: 'ziweiShiyeHepan.toolDesc',
        ctaKey: 'ziweiShiyeHepan.toolCta',
        path: '/tools/ziwei-shiye-hepan',
      },
      {
        icon: 'i-heroicons-users',
        titleKey: 'ziweiZhichangHepan.title',
        descKey: 'ziweiZhichangHepan.toolDesc',
        ctaKey: 'ziweiZhichangHepan.toolCta',
        path: '/tools/ziwei-zhichang-hepan',
      },
      {
        icon: 'i-heroicons-heart',
        titleKey: 'home.toolBaziHunpanTitle',
        descKey: 'home.toolBaziHunpanDesc',
        ctaKey: 'home.toolBaziHunpanCta',
        path: '/tools/bazi-hunpan',
        recommended: true,
      },
      {
        icon: 'i-heroicons-heart',
        titleKey: 'ziweiHunpan.title',
        descKey: 'ziweiHunpan.toolDesc',
        ctaKey: 'ziweiHunpan.toolCta',
        path: '/tools/ziwei-hunpan',
      },
      {
        icon: 'i-heroicons-users',
        titleKey: 'baziPoxiHepan.title',
        descKey: 'baziPoxiHepan.toolDesc',
        ctaKey: 'baziPoxiHepan.toolCta',
        path: '/tools/bazi-poxi-hepan',
      },
      {
        icon: 'i-heroicons-heart',
        titleKey: 'home.toolShengxiaoPeiduiTitle',
        descKey: 'home.toolShengxiaoPeiduiDesc',
        ctaKey: 'home.toolShengxiaoPeiduiCta',
        path: '/tools/shengxiao-peidui',
      },
    ],
  },
  {
    id: 'auspicious-datetime',
    icon: 'i-heroicons-calendar',
    titleKey: 'tools.categoryAuspiciousDatetime',
    subtitleKey: 'tools.categoryAuspiciousDatetimeSubtitle',
    sectionPath: '/auspicious-datetime',
    tools: [
      {
        icon: 'i-heroicons-sun',
        titleKey: 'home.toolHuangdaoTitle',
        descKey: 'home.toolHuangdaoDesc',
        ctaKey: 'home.toolHuangdaoCta',
        path: '/tools/huangdao',
        recommended: true,
      },
      {
        icon: 'i-heroicons-clock',
        titleKey: 'home.toolJishiTitle',
        descKey: 'home.toolJishiDesc',
        ctaKey: 'home.toolJishiCta',
        path: '/tools/jishi',
      },
      {
        icon: 'i-heroicons-gift',
        titleKey: 'home.toolPoufuchanZeriTitle',
        descKey: 'home.toolPoufuchanZeriDesc',
        ctaKey: 'home.toolPoufuchanZeriCta',
        path: '/tools/poufuchan-zeri',
      },
      {
        icon: 'i-heroicons-scale',
        titleKey: 'home.toolJinriYijiTitle',
        descKey: 'home.toolJinriYijiDesc',
        ctaKey: 'home.toolJinriYijiCta',
        path: '/tools/jinri-yiji',
      },
      {
        icon: 'i-heroicons-calendar-days',
        titleKey: 'todayAlmanac.title',
        descKey: 'todayAlmanac.subtitle',
        ctaKey: 'todayAlmanac.tearAction',
        path: '/tools/jinri-huangli',
      },
      {
        icon: 'i-heroicons-calendar-days',
        titleKey: 'almanacCalendar.title',
        descKey: 'almanacCalendar.subtitle',
        ctaKey: 'almanacCalendar.calendar.subscribe',
        path: '/tools/almanac-calendar',
      },
      {
        icon: 'i-heroicons-sparkles',
        titleKey: 'home.toolJinriYunshiTitle',
        descKey: 'home.toolJinriYunshiDesc',
        ctaKey: 'home.toolJinriYunshiCta',
        path: '/tools/jinri-yunshi',
      },
      {
        icon: 'i-heroicons-swatch',
        titleKey: 'home.toolWuxingChuanyiTitle',
        descKey: 'home.toolWuxingChuanyiDesc',
        ctaKey: 'home.toolWuxingChuanyiCta',
        path: '/tools/wuxing-chuanyi',
      },
      {
        icon: 'i-heroicons-arrows-right-left',
        titleKey: 'home.toolChongShengxiaoTitle',
        descKey: 'home.toolChongShengxiaoDesc',
        ctaKey: 'home.toolChongShengxiaoCta',
        path: '/tools/chong-shengxiao',
      },
      {
        icon: 'i-heroicons-banknotes',
        titleKey: 'home.toolShengxiaoPiancaiyunTitle',
        descKey: 'home.toolShengxiaoPiancaiyunDesc',
        ctaKey: 'home.toolShengxiaoPiancaiyunCta',
        path: '/tools/shengxiao-piancaiyun',
      },
      {
        icon: 'i-heroicons-calendar-days',
        titleKey: 'home.toolLiunianTitle',
        descKey: 'home.toolLiunianDesc',
        ctaKey: 'home.toolLiunianCta',
        path: '/tools/liunian',
      },
      {
        icon: 'i-heroicons-truck',
        titleKey: 'home.toolTicheZeriTitle',
        descKey: 'home.toolTicheZeriDesc',
        ctaKey: 'home.toolTicheZeriCta',
        path: '/tools/tiche-zeri',
      },
    ],
  },
  {
    id: 'seeking',
    icon: 'i-heroicons-magnifying-glass',
    titleKey: 'tools.categorySeeking',
    subtitleKey: 'tools.categorySeekingSubtitle',
    sectionPath: '/seeking',
    tools: [
      {
        icon: 'i-heroicons-signal',
        titleKey: 'qimenSeeking.title',
        descKey: 'seeking.toolQimenSeekingDesc',
        ctaKey: 'seeking.toolQimenSeekingCta',
        path: '/tools/qimen-seeking',
        recommended: true,
      },
      {
        icon: 'i-heroicons-circle-stack',
        titleKey: 'liuyaoSeeking.title',
        descKey: 'seeking.toolLiuyaoSeekingDesc',
        ctaKey: 'seeking.toolLiuyaoSeekingCta',
        path: '/tools/liuyao-seeking',
      },
      {
        icon: 'i-heroicons-eye',
        titleKey: 'liurenSeeking.title',
        descKey: 'seeking.toolLiurenSeekingDesc',
        ctaKey: 'seeking.toolLiurenSeekingCta',
        path: '/tools/liuren-seeking',
      },
      {
        icon: 'i-heroicons-hand-raised',
        titleKey: 'xiaoLiurenSeeking.title',
        descKey: 'seeking.toolXiaoLiurenSeekingDesc',
        ctaKey: 'seeking.toolXiaoLiurenSeekingCta',
        path: '/tools/xiao-liuren-seeking',
      },
    ],
  },
  {
    id: 'naming',
    icon: 'i-heroicons-pencil-square',
    titleKey: 'tools.categoryNaming',
    subtitleKey: 'tools.categoryNamingSubtitle',
    sectionPath: '/naming',
    tools: [
      {
        icon: 'i-heroicons-gift',
        titleKey: 'babyNaming.title',
        descKey: 'naming.toolBabyNamingDesc',
        ctaKey: 'naming.toolBabyNamingCta',
        path: '/tools/baby-naming',
        recommended: true,
      },
      {
        icon: 'i-heroicons-sparkles',
        titleKey: 'home.toolSancaiWugeTitle',
        descKey: 'home.toolSancaiWugeDesc',
        ctaKey: 'home.toolSancaiWugeCta',
        path: '/tools/sancai-wuge',
        recommended: true,
      },
      {
        icon: 'i-heroicons-fire',
        titleKey: 'home.toolBaziNamingTitle',
        descKey: 'home.toolBaziNamingDesc',
        ctaKey: 'home.toolBaziNamingCta',
        path: '/tools/bazi-naming',
      },
      {
        icon: 'i-heroicons-squares-2x2',
        titleKey: 'home.toolWugeTitle',
        descKey: 'home.toolWugeDesc',
        ctaKey: 'home.toolWugeCta',
        path: '/tools/wuge',
      },
      {
        icon: 'i-heroicons-heart',
        titleKey: 'nameCompatibility.title',
        descKey: 'nameCompatibility.subtitle',
        ctaKey: 'nameCompatibility.submit',
        path: '/tools/name-compatibility',
      },
      {
        icon: 'i-heroicons-star',
        titleKey: 'home.toolNameScoreTitle',
        descKey: 'home.toolNameScoreDesc',
        ctaKey: 'home.toolNameScoreCta',
        path: '/tools/name-score',
      },
      {
        icon: 'i-heroicons-numbered-list',
        titleKey: 'home.toolNumerologyTitle',
        descKey: 'home.toolNumerologyDesc',
        ctaKey: 'home.toolNumerologyCta',
        path: '/tools/numerology',
      },
      {
        icon: 'i-heroicons-scale',
        titleKey: 'home.toolChengguTitle',
        descKey: 'home.toolChengguDesc',
        ctaKey: 'home.toolChengguCta',
        path: '/tools/chenggu',
      },
    ],
  },
  {
    id: 'cezi',
    icon: 'i-heroicons-pencil',
    titleKey: 'tools.categoryCezi',
    subtitleKey: 'tools.categoryCeziSubtitle',
    sectionPath: '/cezi',
    tools: [
      {
        icon: 'i-heroicons-pencil-square',
        titleKey: 'ceziZhouyi.title',
        descKey: 'ceziZhouyi.toolDesc',
        ctaKey: 'ceziZhouyi.toolCta',
        path: '/tools/cezi-zhouyi',
        recommended: true,
      },
      {
        icon: 'i-heroicons-document-text',
        titleKey: 'ceziYishu.title',
        descKey: 'ceziYishu.toolDesc',
        ctaKey: 'ceziYishu.toolCta',
        path: '/tools/cezi-yishu',
      },
      {
        icon: 'i-heroicons-square-3-stack-3d',
        titleKey: 'qimenZizhan.title',
        descKey: 'qimenZizhan.toolDesc',
        ctaKey: 'qimenZizhan.toolCta',
        path: '/tools/qimen-zizhan',
      },
      {
        icon: 'i-heroicons-circle-stack',
        titleKey: 'liuyaoCezi.title',
        descKey: 'liuyaoCezi.toolDesc',
        ctaKey: 'liuyaoCezi.toolCta',
        path: '/tools/liuyao-cezi',
      },
      {
        icon: 'i-heroicons-bolt',
        titleKey: 'ceziBattle.title',
        descKey: 'ceziBattle.toolDesc',
        ctaKey: 'ceziBattle.toolCta',
        path: '/tools/cezi-battle',
      },
      {
        icon: 'i-heroicons-pencil',
        titleKey: 'liurenZizhan.title',
        descKey: 'liurenZizhan.toolDesc',
        ctaKey: 'liurenZizhan.toolCta',
        path: '/tools/liuren-zizhan',
      },
    ],
  },
  {
    id: 'draw-a-lot',
    icon: 'i-heroicons-gift-top',
    titleKey: 'tools.categoryDrawALot',
    subtitleKey: 'tools.categoryDrawALotSubtitle',
    sectionPath: '/draw-a-lot',
    tools: [
      {
        icon: 'i-heroicons-gift-top',
        titleKey: 'drawALot.title',
        descKey: 'drawALot.toolDesc',
        ctaKey: 'drawALot.toolCta',
        path: '/tools/guanyin-lots',
        recommended: true,
      },
      {
        icon: 'i-heroicons-sparkles',
        titleKey: 'omikuji.title',
        descKey: 'omikuji.toolDesc',
        ctaKey: 'omikuji.draw',
        path: '/tools/omikuji',
      },
      {
        icon: 'i-heroicons-heart',
        titleKey: 'yuelaoLot.title',
        descKey: 'yuelaoLot.toolDesc',
        ctaKey: 'yuelaoLot.toolCta',
        path: '/tools/yuelao-lot',
      },
      {
        icon: 'i-heroicons-currency-dollar',
        titleKey: 'wealthGodLot.title',
        descKey: 'wealthGodLot.toolDesc',
        ctaKey: 'wealthGodLot.toolCta',
        path: '/tools/5-god-of-wealth-lot',
      },
      {
        icon: 'i-heroicons-flag',
        titleKey: 'sanshanwangLot.title',
        descKey: 'sanshanwangLot.toolDesc',
        ctaKey: 'sanshanwangLot.toolCta',
        path: '/tools/3shanwang-lot',
      },
      {
        icon: 'i-heroicons-heart',
        titleKey: 'mazuLot.title',
        descKey: 'mazuLot.toolDesc',
        ctaKey: 'mazuLot.toolCta',
        path: '/tools/mazu-lot',
      },
      {
        icon: 'i-heroicons-shield-check',
        titleKey: 'guandiLot.title',
        descKey: 'guandiLot.toolDesc',
        ctaKey: 'guandiLot.toolCta',
        path: '/tools/guandi-lot',
      },
      {
        icon: 'i-heroicons-moon',
        titleKey: 'wongTaiSinLot.title',
        descKey: 'wongTaiSinLot.toolDesc',
        ctaKey: 'wongTaiSinLot.toolCta',
        path: '/tools/wong-tai-sin-lot',
      },
      {
        icon: 'i-heroicons-academic-cap',
        titleKey: 'wenshuLot.title',
        descKey: 'wenshuLot.toolDesc',
        ctaKey: 'wenshuLot.toolCta',
        path: '/tools/wenshu-lot',
      },
      {
        icon: 'i-heroicons-globe-alt',
        titleKey: 'dizangLot.title',
        descKey: 'dizangLot.toolDesc',
        ctaKey: 'dizangLot.toolCta',
        path: '/tools/dizang-lot',
      },
      {
        icon: 'i-heroicons-home',
        titleKey: 'tudigongLot.title',
        descKey: 'tudigongLot.toolDesc',
        ctaKey: 'tudigongLot.toolCta',
        path: '/tools/tudigong-lot',
      },
      {
        icon: 'i-heroicons-heart',
        titleKey: 'baoshengLot.title',
        descKey: 'baoshengLot.toolDesc',
        ctaKey: 'baoshengLot.toolCta',
        path: '/tools/baosheng-lot',
      },
      {
        icon: 'i-heroicons-numbered-list',
        titleKey: 'zhugeCezi.title',
        descKey: 'zhugeCezi.toolDesc',
        ctaKey: 'zhugeCezi.toolCta',
        path: '/tools/zhuge-cezi',
      },
     {
      icon: 'i-heroicons-hand-raised',
      titleKey: 'jiaobei.title',
      descKey: 'jiaobei.toolDesc',
      ctaKey: 'jiaobei.toolCta',
      path: '/tools/jiaobei',
    },
    {
      icon: 'i-heroicons-moon',
      titleKey: 'home.toolDreamTitle',
      descKey: 'home.toolDreamDesc',
      ctaKey: 'home.toolDreamCta',
      path: '/tools/dream',
      recommended: true,
    },
   ],
 },
  {
    id: 'psychological-test',
    icon: 'i-heroicons-beaker',
    titleKey: 'tools.categoryPsychologicalTest',
    subtitleKey: 'tools.categoryPsychologicalTestSubtitle',
    sectionPath: '/psychological-test',
    tools: [
      {
        icon: 'i-heroicons-chart-pie',
        titleKey: 'psychologicalTest.toolBaziPersonalityTitle',
        descKey: 'psychologicalTest.toolBaziPersonalityDesc',
        ctaKey: 'psychologicalTest.toolBaziPersonalityCta',
        path: '/tools/bazi-personality-map',
        recommended: true,
      },
      {
        icon: 'i-heroicons-heart',
        titleKey: 'psychologicalTest.toolParentChildBaziTitle',
        descKey: 'psychologicalTest.toolParentChildBaziDesc',
        ctaKey: 'psychologicalTest.toolParentChildBaziCta',
        path: '/tools/parent-child-bazi',
      },
      {
        icon: 'i-heroicons-home',
        titleKey: 'psychologicalTest.toolParentingStyleTitle',
        descKey: 'psychologicalTest.toolParentingStyleDesc',
        ctaKey: 'psychologicalTest.toolParentingStyleCta',
        path: '/tools/parenting-style',
      },
      {
        icon: 'i-heroicons-heart',
        titleKey: 'psychologicalTest.toolMarriageXiangxingTitle',
        descKey: 'psychologicalTest.toolMarriageXiangxingDesc',
        ctaKey: 'psychologicalTest.toolMarriageXiangxingCta',
        path: '/tools/marriage-xiangxing',
      },
      {
        icon: 'i-heroicons-puzzle-piece',
        titleKey: 'psychologicalTest.toolChildActivityInterestTitle',
        descKey: 'psychologicalTest.toolChildActivityInterestDesc',
        ctaKey: 'psychologicalTest.toolChildActivityInterestCta',
        path: '/tools/child-activity-interest',
      },
      {
        icon: 'i-heroicons-face-smile',
        titleKey: 'psychologicalTest.toolSbtiTitle',
        descKey: 'psychologicalTest.toolSbtiDesc',
        ctaKey: 'psychologicalTest.toolSbtiCta',
        path: '/tools/sbti',
      },
      {
        icon: 'i-heroicons-trophy',
        titleKey: 'psychologicalTest.toolLifeAchievementTitle',
        descKey: 'psychologicalTest.toolLifeAchievementDesc',
        ctaKey: 'psychologicalTest.toolLifeAchievementCta',
        path: '/tools/life-achievement-generator',
        recommended: true,
      },
      {
        icon: 'i-heroicons-bolt',
        titleKey: 'workCheer.title',
        descKey: 'workCheer.subtitle',
        ctaKey: 'common.start',
        path: '/tools/slaves-encourager',
      },
      {
        icon: 'i-heroicons-cake',
        titleKey: 'bztiBirthdayPersonality.title',
        descKey: 'bztiBirthdayPersonality.subtitle',
        ctaKey: 'common.start',
        path: '/tools/bzti-birthday-personality',
      },
      {
        icon: 'i-heroicons-shield-exclamation',
        titleKey: 'fuckClaude.toolTitle',
        descKey: 'fuckClaude.toolDesc',
        ctaKey: 'fuckClaude.toolCta',
        path: '/tools/fuckclaude',
      },
      {
        icon: 'i-heroicons-queue-list',
        titleKey: 'psychologicalTest.toolJapanPriorityTestTitle',
        descKey: 'psychologicalTest.toolJapanPriorityTestDesc',
        ctaKey: 'psychologicalTest.toolJapanPriorityTestCta',
        path: '/tools/japan-priority-test',
      },
    ],
  },
  {
    id: 'prophet',
    icon: 'i-heroicons-trophy',
    titleKey: 'tools.categoryProphet',
    subtitleKey: 'tools.categoryProphetSubtitle',
    sectionPath: '/prophet',
    tools: [
      {
        icon: 'i-heroicons-circle-stack',
        titleKey: 'home.toolLiuyaoFootballTitle',
        descKey: 'home.toolLiuyaoFootballDesc',
        ctaKey: 'home.toolLiuyaoFootballCta',
        path: '/prophet/liuyao-football',
        recommended: true,
      },
      {
        icon: 'i-heroicons-squares-2x2',
        titleKey: 'home.toolQimenFootballTitle',
        descKey: 'home.toolQimenFootballDesc',
        ctaKey: 'home.toolQimenFootballCta',
        path: '/prophet/qimen-football',
      },
      {
        icon: 'i-heroicons-cube-transparent',
        titleKey: 'home.toolLiurenFootballTitle',
        descKey: 'home.toolLiurenFootballDesc',
        ctaKey: 'home.toolLiurenFootballCta',
        path: '/prophet/liuren-football',
      },
      {
        icon: 'i-heroicons-sparkles',
        titleKey: 'home.toolLiuyaoTitle',
        descKey: 'home.toolLiuyaoDesc',
        ctaKey: 'home.toolLiuyaoCta',
        path: '/tools/liu-yao',
      },
      {
        icon: 'i-heroicons-squares-2x2',
        titleKey: 'home.toolQimenWorldcupTitle',
        descKey: 'home.toolQimenWorldcupDesc',
        ctaKey: 'home.toolQimenWorldcupCta',
        path: '/prophet/qimen-worldcup',
      },
      {
        icon: 'i-heroicons-cube-transparent',
        titleKey: 'home.toolLiurenWorldcupTitle',
        descKey: 'home.toolLiurenWorldcupDesc',
        ctaKey: 'home.toolLiurenWorldcupCta',
        path: '/prophet/liuren-worldcup',
      },
      {
        icon: 'i-heroicons-user-group',
        titleKey: 'home.toolFbtiTitle',
        descKey: 'home.toolFbtiDesc',
        ctaKey: 'home.toolFbtiCta',
        path: '/tools/fbti',
      },
      {
        icon: 'i-heroicons-trophy',
        titleKey: 'home.toolWorldcupChampionTitle',
        descKey: 'home.toolWorldcupChampionDesc',
        ctaKey: 'home.toolWorldcupChampionCta',
        path: '/prophet/worldcup-champion-odds-2026',
      },
    ],
  },
]

const analysisToolPaths = [
  '/tools/qimen',
  '/tools/bazi',
  '/tools/zecheng',
  '/tools/zwds',
  '/tools/zhouyi',
  '/tools/liuyao-divination',
  '/tools/liuren',
  '/tools/xiao-liuren',
  '/tools/jinkoujue',
  '/tools/taiyi',
  '/tools/bazhai-fengshui',
  '/tools/huangji-zhiniangua',
  '/tools/jinsuoyuguan-fengshui',
  '/tools/xuankong-fengshui',
  '/tools/qizheng-siyu',
  '/tools/tarot',
  '/tools/thai-tarot',
  '/tools/lenormand',
] as const

const numericEnergyCategory = analysisToolCategories.find(category => category.id === 'numeric-energy')!
const prophetCategory = analysisToolCategories.find(category => category.id === 'prophet')!
const orderedAnalysisToolCategories = [
  ...analysisToolCategories.filter(category => !['numeric-energy', 'prophet'].includes(category.id)),
  numericEnergyCategory,
  prophetCategory,
]

export const toolCategories: ToolCategory[] = [
  chartingToolCategory,
  ...orderedAnalysisToolCategories.map(category => ({
    ...category,
    group: 'fortune-analysis' as const,
  })),
]

function findRegisteredTool(path: string): ToolItem {
  const tool = toolCategories
    .flatMap(category => category.tools)
    .find(item => item.path === path)

  if (!tool) {
    throw new Error(`Unregistered tool directory path: ${path}`)
  }

  return tool
}

const chartingToolItems = chartingToolCategory.tools
const analysisToolItems = analysisToolPaths.map(path => findRegisteredTool(path))

export const toolDirectoryGroups: ToolDirectoryGroup[] = [
  {
    id: 'charting',
    icon: 'i-heroicons-table-cells',
    titleKey: 'toolDirectories.chartTitle',
    subtitleKey: 'toolDirectories.chartSubtitle',
    path: '/chart',
    links: chartingToolItems.map(tool => ({
      icon: tool.icon,
      titleKey: tool.titleKey,
      subtitleKey: tool.descKey,
      path: tool.path,
      kind: 'tool' as const,
    })),
  },
  {
    id: 'fortune-analysis',
    icon: 'i-heroicons-sparkles',
    titleKey: 'toolDirectories.analysisTitle',
    subtitleKey: 'toolDirectories.analysisSubtitle',
    path: '/tools',
    links: analysisToolItems.map(tool => ({
      icon: tool.icon,
      titleKey: tool.titleKey,
      subtitleKey: tool.descKey,
      path: tool.path,
      kind: 'tool' as const,
    })),
  },
  {
    id: 'special',
    icon: 'i-heroicons-rectangle-group',
    titleKey: 'toolDirectories.specialTitle',
    subtitleKey: 'toolDirectories.specialSubtitle',
    path: '/special',
    links: [
      {
        icon: 'i-heroicons-sparkles',
        titleKey: 'fortune2026.entryTitle',
        subtitleKey: 'fortune2026.subtitle',
        path: '/fortune/2026',
        kind: 'topic' as const,
      },
      {
        icon: 'i-heroicons-calendar-days',
        titleKey: 'todayAlmanac.title',
        subtitleKey: 'todayAlmanac.subtitle',
        path: '/tools/jinri-huangli',
        kind: 'tool' as const,
      },
      ...toolCategories.filter(category => category.id !== 'paipan').map(category => ({
        icon: category.icon,
        titleKey: category.titleKey,
        subtitleKey: category.subtitleKey,
        path: category.sectionPath,
        kind: 'topic' as const,
      })),
    ],
  },
]

export function useToolDirectoryGroups(): ComputedRef<ToolDirectoryGroup[]> {
  return computed(() => toolDirectoryGroups)
}

export function useChartingTools(): ComputedRef<ToolItem[]> {
  return computed(() => chartingToolItems)
}

export function useAnalysisTools(): ComputedRef<ToolItem[]> {
  return computed(() => analysisToolItems)
}

/**
 * 所有工具专题分类数据
 * 供 /tools 专题列表页读取，各专题页亦可复用
 */
export function useToolCategories(): ComputedRef<ToolCategory[]> {
  return computed(() => toolCategories)
}

export function useSpecialToolCategories(): ComputedRef<ToolCategory[]> {
  return computed(() => toolCategories.filter(category => category.id !== 'paipan'))
}

/**
 * 获取某个专题下除前 3 个展示外剩余的完整工具列表
 * 用于「更多」跳转后的专题页渲染
 */
export function getCategoryFullTools(categoryId: string): ToolItem[] {
  const cat = toolCategories.find(c => c.id === categoryId)
  return cat?.tools ?? []
}

/**
 * 获取所有工具的扁平列表（用于 SEO JSON-LD）
 */
export function getAllToolsFlat(): ToolItem[] {
  return toolCategories.flatMap(c => c.tools)
}
